import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Store} from '@ngrx/store';
import {AppState} from '../../app/app.state';
import {SessionService} from './session.service';
import {Hub, HubId, LorawanGateway} from '../../shared/models/hub';
import {HubFetchedAction, HubSavedToApiAction, HubsUserFetchedAction, HubTunnelCheckAction} from '../actions/hubs.actions';
import {BehaviorSubject, Observable, of, Subject, Subscription} from 'rxjs';
import {API_BASE_URL, TUNNEL_BASE_URL} from './env.service';
import {HubStatus} from '../../shared/models/hub-status';
import {BacnetDevice} from '../../shared/models/bacnet';
import {HubIdMap} from '../state/hubs.state';

export const HUB_FETCH_INTERVAL = 10000;

@Injectable({
  providedIn: 'root',
})
export class HubsService {
  private fetchedUserHubsOnce: boolean = false;

  sessUserHubs: HubIdMap = {};
  sessUserHubs$: BehaviorSubject<HubIdMap> = new BehaviorSubject<HubIdMap>({});

  /**
   * If a fetch is currently in progress, this will not be empty.
   * @private
   */
  private fetchUserHubsResults$: Subject<Hub[]>;

  bacnetDeviceDiscoveryCache: { [id: HubId]: BacnetDevice[] } = {};

  /**
   * This will contain the IDs of Hubs for which an AJAX call is currently in flight,
   * so we can prevent simultaneous requests for the same Hub
   */
  private httpStatusSubs: { [hubId: HubId]: Subscription } = {};

  private fetchStatusSubjects: { [hubId: HubId]: Subject<HubStatus> } = {};


  constructor(
    private http: HttpClient,
    private session: SessionService,
    private store: Store<AppState>,
  ) {
    this.store.select('hubs').subscribe(
      (state) => {
        for (let hub of Object.values(state.hubs)) {
          this.sessUserHubs[hub.ID] = hub;
        }

        this.sessUserHubs$.next(this.sessUserHubs);
      },
    );
  }

  /**
   * Returns an observable that emits the user's hubs. More critically,
   * if user hubs have already been fetched from server, returns an
   * observable that emits that cached array; otherwise triggers a fetch
   * from the API and returns observable that emits the response from
   * the server.
   */
  getUserHubs$(): Observable<Hub[]> {
    if (this.fetchedUserHubsOnce) {
      return of(Object.values(this.sessUserHubs));
    }

    if (this.fetchUserHubsResults$) {
      return this.fetchUserHubsResults$;
    }

    this.fetchUserHubsResults$ = new Subject<Hub[]>();

    this.fetchUserHubs$().subscribe({
      next: (hubs) => {
        this.fetchUserHubsResults$.next(hubs);
        this.fetchUserHubsResults$.complete();
        delete this.fetchUserHubsResults$;
      },
    });

    return this.fetchUserHubsResults$;
  }


  /**
   * Call this to get user hubs from the API directly with no cache.
   */
  fetchUserHubs$(): Observable<Hub[]> {
    return new Observable<Hub[]>(
      subscriber => {
        this.http.get<Hub[]>(`${TUNNEL_BASE_URL}/hubs/user/${this.session.userId}`).subscribe(
          (hubs) => {
            this.store.dispatch(new HubsUserFetchedAction(hubs));

            subscriber.next(hubs);
            subscriber.complete();

            this.fetchedUserHubsOnce = true;
          },
          (err) => {
            subscriber.error(err);
          },
        );
      },
    );
  }


  fetchHubStatus$(hubId: HubId): Subject<HubStatus> {
    let status$: Subject<HubStatus>;

    if (typeof this.fetchStatusSubjects[hubId] !== 'undefined') {
      status$ = this.fetchStatusSubjects[hubId];
    } else {
      status$ = this.fetchStatusSubjects[hubId] = new Subject<HubStatus>();
    }

    if (!this.httpStatusSubs[hubId]) {
      this.httpStatusSubs[hubId] = this.http.get<HubStatus>(`${TUNNEL_BASE_URL}/remote/tunnel-test/hub/${hubId}`).subscribe(
        (status) => {
          this.store.dispatch(new HubTunnelCheckAction(hubId, status));

          status$.next(status);
          status$.complete();
          delete this.httpStatusSubs[hubId];
          delete this.fetchStatusSubjects[hubId];
        },
        (err) => {
          // The API will return a 500 error if it tried talking to the Hub and failed.
          // It would only try to talk to the Hub if the Hub had recently successfully
          // authenticated. So if the Hub has a crappy connection, but is technically
          // connected to the internet, we'd expect this to be the result. Therefore
          // we null out status to avoid definitively saying the tunnel test was
          // successful or not.
          this.store.dispatch(new HubTunnelCheckAction(hubId, null));

          status$.error(err);
          delete this.httpStatusSubs[hubId];
          delete this.fetchStatusSubjects[hubId];
        },
      );
    }

    return status$;
  }


  /**
   * Cancels an in-progress hub status request, if one exists.
   *
   * @param hubId
   */
  cancelFetchHubStatus(hubId: HubId): void {
    if (typeof this.httpStatusSubs[hubId] !== 'undefined') {
      this.httpStatusSubs[hubId].unsubscribe();
      delete this.httpStatusSubs[hubId];
      delete this.fetchStatusSubjects[hubId];
    }
  }


  fetchLorawanGateways$(hubId: HubId): Observable<LorawanGateway[]> {
    return this.http.get<LorawanGateway[]>(`${API_BASE_URL}/hubs/${hubId}/lorawan-gateways`);
  }


  saveLorawanGateways$(hubId: HubId, euis: string[]): Observable<LorawanGateway[]> {
    return this.http.put<LorawanGateway[]>(`${API_BASE_URL}/hubs/${hubId}/lorawan-gateways`, {
      EUIs: euis,
    });
  }


  activateHub$(hubId: HubId, code: string): Observable<Hub> {
    return new Observable<Hub>(
      subscriber => {
        this.http.post<Hub>(`${API_BASE_URL}/hub-activations`, {
          Hub: hubId,
          Owner: this.session.userId,
          Code: code,
        }).subscribe(
          (hub) => {
            this.store.dispatch(new HubFetchedAction(hub));

            subscriber.next(hub);
            subscriber.complete();
          },
          (err) => {
            subscriber.error(err);
          },
        );
      },
    );
  }


  saveChangesToApi$(hub: Hub): Observable<null> {
    return new Observable<null>(
      subscriber => {
        this.http.put<null>(`${TUNNEL_BASE_URL}/hubs/${hub.ID}`, hub).subscribe(
          () => {
            this.store.dispatch(new HubSavedToApiAction(JSON.parse(JSON.stringify(hub))));

            subscriber.next(null);
            subscriber.complete();
          },
          (err) => {
            subscriber.error(err);
          },
        );
      },
    );
  }


  /**
   * After initiating discovery, unsubscribe to the returned observable to abort
   * before normal completion.
   *
   * @param hubId
   * @param timeout
   */
  discoverDevicesBacnet$(hubId: HubId, timeout: number = 120): Observable<BacnetDevice[]> {
    return new Observable<BacnetDevice[]>(
      subscriber => {
        const sub = this.http.get<BacnetDevice[]>(`${TUNNEL_BASE_URL}/remote/bacnet/device-discovery`, {
          params: {
            HubID: '' + hubId,
          },
          headers: {
            'x-motion-hubd-timeout': `${timeout}`,
          },
        }).subscribe(
          (data) => {
            data = data.filter(bd => !bd.Error);

            this.bacnetDeviceDiscoveryCache[hubId] = data;

            subscriber.next(data);
            subscriber.complete();
          },
          (err) => {
            subscriber.error(err);
          },
        );

        return () => {
          sub.unsubscribe();
        };
      },
    );
  }
}
